/**
 * FormWizard.js - 5-Step Accordion Wizard Version of the Booking Form
 *
 * This component wraps the existing Form.js logic with the new wizard UI.
 * All business logic, validation, and API calls are preserved.
 */

import React, { useContext, useState, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import configData from '../../config/config.json';
import { useHistory } from "react-router-dom";
import Navbar from "../Navbar";
import "./form.css";
import "./wizard.css";
import { Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Form } from "antd";
import { GlobalContext } from "../../contexts/GlobalState";
import axios from "axios";
import CircularProgress from "@material-ui/core/CircularProgress";
import ErrorModal from "./ErrorModal";
import AncillariesModal from "./AncillariesModal";
import { useToasts } from "react-toast-notifications";
import TermsModal from "./TermsModal.jsx";
import YesNoModal from "./YesNoModal";
import MainNavigationBar from "../../features/Layout/MainNavigationBar";

// Wizard components
import useBookingWizard from './hooks/useBookingWizard';
import useLocalStoragePersistence from './hooks/useLocalStoragePersistence';
import AccordionStep from './components/AccordionStep';
import BookingSidebar from './components/BookingSidebar';
import MobileSummaryBar from './components/MobileSummaryBar';
import BookingDetailsModal from './components/BookingDetailsModal';
import CountdownTimer from './components/CountdownTimer';
import StepBookerDetails from './steps/StepBookerDetails';
import StepPassengerDetails from './steps/StepPassengerDetails';
import StepBaggageSelection from './steps/StepBaggageSelection';
import StepOrderSummary from './steps/StepOrderSummary';
import StepPaymentApproval from './steps/StepPaymentApproval';

window.dataLayer = window.dataLayer || [];
window.dataLayer.push({
  event: 'Pageview',
  'pageUrl': window.location,
  'pageTitle': window.title,
});

const API_ENDPOINT = configData.API_ENDPOINT;

const useStyles = makeStyles((theme) => ({
  root: {},
  inputField: {
    width: "100%",
  },
  Checkbox: {
    paddingTop: "0px",
    paddingBottom: "0px",
  },
}));

const ReservationWizard = (props) => {
  const { t } = useTranslation();
  const package_uid = props.match.params.deal_id;
  const quote_uid = props.match.params.quote_id;
  let room_uid, pax, rooms;

  const { addToast } = useToasts();
  const history = useHistory();

  const classes = useStyles();

  // Context
  const {
    packageDetails,
    preserveData,
    reservationDetails,
    preserveReservation,
    changePassengerQuantity,
    changeSelectedRoom,
    selectedRoomContext
  } = useContext(GlobalContext);

  // Form instance
  const [form] = Form.useForm();

  // Existing state
  const [openError, setOpenError] = useState({});
  const [openAncillaries, setOpenAncillaries] = useState(false);
  const [ancillariesState, setAncillariesState] = useState(false);
  const [termsModal, setTermsModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [yesNoMessage, setYesNoMessage] = useState("");
  const [ancillariesMessage, setAncillariesMessage] = useState("");
  const [termLink, setTermLink] = useState("");
  const [termText, setTermText] = useState("");
  const [reservationState, setReservationState] = useState({});
  const [detailState, setDetailState] = useState({});
  const [baggageState, setBaggageState] = useState([]);
  const [totalBaggagePrice, setTotalBaggagePrice] = useState(0);
  const [openYesNo, setOpenYesNo] = useState(false);
  const [detailRoomState, setDetailRoomState] = useState([]);
  const [selectedRoomUID, setRoomUID] = useState({});
  const [selectedRoom, setSelectedRoom] = useState({});
  const [finishProgress, setFinishProgress] = useState(false);
  const [quoteDetails, setQuoteDetails] = useState({});
  const [paymentsCount, setPaymentsCount] = useState(1);

  // Mobile modal state
  const [mobileModalOpen, setMobileModalOpen] = useState(false);

  // Wizard hooks
  const {
    currentStep,
    completedSteps,
    getStepStatus,
    completeStep,
    goToStep,
    getSummaryForStep,
    stepInfo
  } = useBookingWizard(form, quote_uid);

  const {
    saveFormData,
    loadFormData
  } = useLocalStoragePersistence(quote_uid);

  // Helper functions (preserved from original)
  const GetMealPlanName = (code) => {
    switch (code) {
      case 'HM': return t('hotel.mealPlans.halfBoard');
      case 'MB': return t('hotel.mealPlans.halfBoardWithDrinks');
      case 'MP': return t('hotel.mealPlans.halfBoard');
      case 'PC': return t('hotel.mealPlans.fullBoard');
      case 'PV': return t('hotel.mealPlans.fullBoardWithDrinks');
      case 'SA': return '';
      case 'SP': return '';
      case 'TI': return t('hotel.mealPlans.allInclusive');
      case 'TP': return t('hotel.mealPlans.allInclusivePremium');
      case 'SC': return '';
      case 'HD': return t('hotel.mealPlans.breakfast');
      default: return '';
    }
  };

  const calculateTotalPrice = (basePrice, baggagePrice, paymentsCount) => {
    const paymentsAddedComission = calculatePaymentsCommissionPercentage(paymentsCount);
    const totalPrice = parseFloat(basePrice + baggagePrice) * (paymentsAddedComission + 1);
    return Math.ceil(totalPrice);
  };

  const calculatePaymentPrice = (basePrice, baggagePrice, paymentsCount) => {
    return (calculateTotalPrice(basePrice, baggagePrice, paymentsCount) / paymentsCount).toFixed(2);
  };

  const calculatePaymentsCommissionPercentage = (payments = 1) => {
    switch (payments) {
      case 1: return 0;
      case 2: return 0.015;
      case 3: return 0.025;
      case 4: return 0.03;
      case 5: return 0.035;
      case 6: return 0.04;
      case 7: return 0.045;
      case 8: return 0.05;
      case 9: return 0.055;
      case 10: return 0.06;
      case 11: return 0.065;
      case 12: return 0.07;
      default: return 0.005 * payments + 0.01;
    }
  };

  const generatePaxArray = (adultsCount, childrenArray) => {
    const pax = [];
    for (let i = 0; i < adultsCount; i++) pax.push(30);
    childrenArray.map(age => pax.push(age));
    return pax;
  };

  const getGuestCategory = (age) => {
    if (age < 2) return { code: 'I', name_he: `${t('pax.infants.title')} (${t('pax.infants.ageRange')})`, name: 'infants' };
    if (age <= 18) return { code: 'C', name_he: `${t('pax.children.title')} ${t('pax.children.ageRange')}`, name: 'children' };
    return { code: 'A', name_he: `${t('pax.adults.title')} (${t('pax.adults.ageRange')})`, name: 'adults' };
  };

  // Main effect - Load data (preserved from original)
  useEffect(() => {
    console.log('useEffect - Loading data');

    // Force page refresh after 60 minutes
    setTimeout(() => {
      console.log('Expired... reloading');
      window.location.reload(false);
    }, 60 * 60 * 1000);

    const loadData = async () => {
      try {
        if (detailState.uid === undefined) {
          console.log('Loading data from remote server...');

          let url = `${API_ENDPOINT}/search/packages/${package_uid}/`;
          let res = await axios.get(url, {
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true
          });

          room_uid = res?.data?.property?.room?.uid;
          pax = res?.data?.pax;
          rooms = res?.data?.property?.rooms_occupancies;

          if (res?.data && !res.data?.error) {
            setDetailState(res?.data);

            // Load quote details
            let resQuote = null;
            try {
              resQuote = await axios.get(
                `${API_ENDPOINT}/quote/${quote_uid}`,
                {
                  headers: { 'Content-Type': 'application/json' },
                  withCredentials: true,
                  timeout: 10000
                }
              );
            } catch (e) {
              console.log('Failed to load quote');
              setErrorMessage({
                title: '',
                message: t('reservation.wizard.errors.quoteExpired')
              });
              setOpenError({ status: true, go_back: false });
              return;
            }

            if (res?.data && res?.data.closed != null) {
              setErrorMessage({
                title: '',
                message: t('reservation.wizard.errors.quoteExpired')
              });
              setOpenError({ status: true, go_back: false });
            }

            if (resQuote?.data && !res?.data?.error) {
              setQuoteDetails(resQuote?.data);
            }

            if (resQuote.data?.uid) {
              res.data.price = resQuote.data.price;

              const resFlightBaggage = await axios.get(
                `${API_ENDPOINT}/search/packages/${package_uid}/flight/baggage/?adults=${pax?.adults}&children=${pax?.children}&infants=${pax?.infants}`,
                {
                  headers: { 'Content-Type': 'application/json' },
                  withCredentials: true,
                  timeout: 10000
                }
              );

              setReservationState({
                status: "valid",
                package: res?.data,
                flight: resFlightBaggage?.data,
                property: res?.data?.property,
                session: res?.data?.session,
                job: res?.data?.job,
              });

              // GTM event
              window.dataLayer.push({ ecommerce: null });
              window.dataLayer.push({
                event: quoteDetails?.is_flex ? 'ReserveFlexDeal' : 'ReserveBasicDeal',
                ecommerce: {
                  currencyCode: res?.data?.currency,
                  checkout: {
                    actionField: { step: 1 },
                    products: [{
                      id: res?.data?.uid,
                      name: res?.data?.code,
                      category: res?.data?.destination?.country?.name + '/' + res?.data?.destination?.city?.name,
                      price: res?.data?.price,
                      brand: 'afnu',
                      variant: quoteDetails?.is_flex ? 'flex' : 'standard',
                      quantity: 1
                    }]
                  }
                }
              });
            } else {
              setErrorMessage({ title: t('reservation.wizard.errors.dealNotFound'), message: 'Error 1' });
              setOpenError({ status: true, go_back: true });
            }
          } else {
            setErrorMessage({ title: t('reservation.wizard.errors.dealNotFound'), message: 'Error 2' });
            setOpenError({ status: true, go_back: true });
          }

          const res2 = await axios.get(
            `${API_ENDPOINT}/search/packages/${res?.data.uid}/rooms/${res?.data?.property?.room?.uid}`,
            {
              headers: { 'Content-Type': 'application/json' },
              withCredentials: true
            }
          );

          setDetailRoomState(res2?.data);
          let sr = res2?.data.find((item) => {
            setRoomUID(item.uid);
            return item?.best_offer === true;
          });
          setSelectedRoom(sr);
          preserveData(res?.data, sr);
        }
      } catch (e) {
        console.log('EXCEPTION', e);
        window.dataLayer.push({
          event: 'ModalQuoteNotFound',
          'pageUrl': window.location,
          'pageTitle': window.title
        });
        setErrorMessage({ title: t('reservation.wizard.errors.dealNotFound'), message: e?.message });
        setOpenError({ status: true, go_back: true });
      }
    };

    loadData();

    // Load saved form data from localStorage
    const savedData = loadFormData();
    if (savedData && savedData.formValues) {
      form.setFieldsValue(savedData.formValues);
      if (savedData.baggageState) {
        setBaggageState(savedData.baggageState);

        // Recalculate total baggage price from loaded state
        const recalculatedBaggagePrice = savedData.baggageState.reduce(
          (sum, item) => sum + (item.baggage?.price || 0),
          0
        );
        setTotalBaggagePrice(recalculatedBaggagePrice);
      }

      // Restore payment count from localStorage
      if (savedData.paymentsCount) {
        setPaymentsCount(savedData.paymentsCount);
      }
    }
  }, [packageDetails]);

  // Handlers
  const handleClose = () => {
    setOpenError(false);
    setFinishProgress(false);
  };

  const handleCloseReturnToDeal = () => {
    setOpenError(false);
    setFinishProgress(false);
    const url_array = window.location.pathname.split('/');
    history.push(url_array[0] + '/' + url_array[1] + '/' + url_array[2]);
  };

  const handleCloseAncillaries = () => setOpenAncillaries(false);
  const handleCloseTerms = () => setTermsModal(false);

  const handleYes = () => setOpenYesNo(false);
  const handleNo = () => setOpenYesNo(false);


  const GetMealPlanCode = (code) => {
    let local_meal_plan_code='RO';
    
    switch(code) {
        case 'SA'||'SP'||'SC': local_meal_plan_code='RO';break; // Accommodation only
        case 'HD': local_meal_plan_code='BB';break;             // B&B 
        case 'HM'||'MB'||'MP': local_meal_plan_code='HB';break; // HAlf Board
        case 'PC'||'PV': local_meal_plan_code='FB';break;       // Full board
        case 'TI'||'TP': local_meal_plan_code='AI';break;       // All Inclusive
        default: local_meal_plan_code='RO'
    }

    return local_meal_plan_code;
  }


  const onOpenLink = (e, url, additionalText = '') => {
    e.preventDefault();
    setTermLink(url);
    setTermText(additionalText);
    setTermsModal(true);
  };

  // Baggage change handler (preserved from original)
  const changeBaggage = useCallback((e, passenger_group_code, data, passenger, passenger_index, bag_index, direction, price) => {
    const key = `${bag_index}|${passenger}|${passenger_index}|${direction}|${price}|${passenger_group_code}|${data.baggage.code}`;
    data.key = key;

    // Check if already exists
    const existingIndex = baggageState.findIndex(item => item.key === key);

    if (e === 'checked' || (e.target && e.target.checked)) {
      // Add baggage - use functional update to avoid stale closure issues
      if (existingIndex === -1) {
        setBaggageState(prev => {
          // Double-check in case state changed since function was called
          const stillNotExists = prev.findIndex(item => item.key === key) === -1;
          if (stillNotExists) {
            return [...prev, data];
          }
          return prev;
        });
        setTotalBaggagePrice(prev => prev + price);

        // GTM event
        window.dataLayer.push({
          event: 'AddBag',
          baggage_direction: direction,
          baggage_price: price
        });
      }
    } else {
      // Remove baggage - use functional update and filter by key (not index)
      if (existingIndex !== -1) {
        setBaggageState(prev => prev.filter(item => item.key !== key));
        setTotalBaggagePrice(prev => prev - price);

        // GTM event
        window.dataLayer.push({
          event: 'RemoveBag',
          baggage_direction: direction,
          baggage_price: price
        });
      }
    }
  }, [baggageState]);

  // Form submission (preserved from original)
  const onFinish = async (values) => {
    console.log('onFinish', values);
    setFinishProgress(true);

    try {
      const dataToSend = {
        package_uid: package_uid,
        rooms: packageDetails?.data?.property?.rooms_occupancies,
        owner: {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone: values.phone
        },
        passengers: values.passengers,
        pax: packageDetails?.data?.pax,
        ancillaries: reservationState?.package?.ancillaries,
        baggage: baggageState,
        payments_count: paymentsCount,
        marketing_consent: values.marketing_consent || false,
        consent_version: values.consent_version || null
      };

      const response = await axios.post(
        `${API_ENDPOINT}/quote/${quote_uid}`,
        dataToSend,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        }
      );

        // GTM event
        console.log('GTM event: ' + 'booking_step_5_complete')

        
        window.dataLayer.push({
            event: 'booking_step_5_complete',
            step_number: 5,
            step_name: 'payment_approval',
            quote_uid: quote_uid,
            pageUrl: window.location.href
        });


        // redirect instead of using iframe
            
            window.dataLayer.push({ ecommerce: null });  
            window.dataLayer.push({
                event: 'ReservationFormSubmit',
                'pageUrl': window.location,
                'pageTitle' : window.title,
                
                ecommerce: {
                  checkout: {
                    actionField: {
                      step: 2,
                      option: 'Order Form Submit'
                    },
                    /*products: [{
                      id: res?.data?.uid,
                      name: res?.data?.code,
                      category: res?.data?.destination?.country?.name+'/'+res?.data?.destination?.city?.name,
                      price: res?.data?.price,
                      brand: 'afnu',
                      variant: quoteDetails?.is_flex?'flex':'standard',
                      quantity: 1
                    }]*/ 
                  }
                },
                /*
                ecommerce: {
                    currencyCode: res?.data?.currency,
                    checkout: {
                      actionField: {
                        step: 1
                      },
                      products: [{
                          id: res?.data?.uid,
                          name: res?.data?.code,
                          category: res?.data?.destination?.country?.name+'/'+res?.data?.destination?.city?.name,
                          price: res?.data?.price,
                          brand: 'afnu',
                          variant: quoteDetails?.is_flex?'flex':'standard',
                          quantity: 1
                      }]
                    }
                }  
                */
            });

            window.dataLayer.push({
              event: "begin_checkout",
              ecommerce: {
                currency: quoteDetails?.currency,
                value: quoteDetails?.price,
                items: [
                {
                  item_id: quoteDetails?.uid,
                  item_name: quoteDetails.flights[0]?.outbound?.airport_code_arrival+quoteDetails?.id, // deal.to_iata+deal.id
                  index: 0,
                  item_brand: quoteDetails?.destination?.city_name_en,
                  //item_category: quoteDetails?.is_flex?'flex':'standard',
                  item_category: `adults:${quoteDetails?.adults},children:${quoteDetails?.children},infants:${quoteDetails?.infants},rooms:${quoteDetails?.property?.rooms_count}`,
                  item_category2: "stars: " + (quoteDetails?.properties && quoteDetails?.properties.length>0 ? (quoteDetails?.properties[0]?.stars):0) + " meals: " + GetMealPlanCode(quoteDetails?.properties[0]?.inventory[0]?.rooms[0]?.meal_code),
                  item_category3: "nights: " + quoteDetails?.nights,
                  item_category4: "departure: " + GetTimeToDeparture(quoteDetails?.from_date),
                  item_category5: "month: "+ quoteDetails?.from_date.split("-", 2).join("-"), // remove everything after second dash occurance (which converts the date from 2023-07-29T00:00:00.000Z to 2023-07),
                  item_list_id:   "deals",
                  item_list_name: "Deals",
                  item_variant:   quoteDetails?.is_flex?'flex':'standard',
                  location_id: quoteDetails?.destination?.city_id,
                  price:  quoteDetails?.price,
                  quantity: 1
                }
                ]
              },
              eventCallback: function() {
                document.location = response?.data?.url;
              }
            });

      if (response.data?.url) {
        // GTM event
        window.dataLayer.push({
          event: 'ReservationFormSubmit',
          quote_uid: quote_uid,
          total_price: calculateTotalPrice(reservationState?.package?.price, totalBaggagePrice, paymentsCount)
        });

        // Redirect to payment
        window.location.href = response.data.url;
      } else {
        setErrorMessage({
          title: t('package.modals.error.title'),
          message: t('reservation.wizard.errors.submissionError')
        });
        setOpenError({ status: true, go_back: false });
        setFinishProgress(false);
      }
    } catch (error) {
      console.error('Submission error:', error);
      setErrorMessage({
        title: t('package.modals.error.title'),
        message: error?.response?.data?.message || t('reservation.wizard.errors.submissionError')
      });
      setOpenError({ status: true, go_back: false });
      setFinishProgress(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Form validation failed:', errorInfo);
    addToast(t('reservation.wizard.errors.validationError'), { appearance: 'error' });
  };

  const handleValuesChange = (changedValues, allValues) => {
    // Save to localStorage on change
    saveFormData({
      formValues: allValues,
      baggageState: baggageState,
      wizardState: { currentStep, completedSteps: Array.from(completedSteps) },
      paymentsCount: paymentsCount
    });
  };

  const handlePaymentsChange = (newPaymentsCount) => {
    // Update state
    setPaymentsCount(newPaymentsCount);

    // Immediately save to localStorage
    saveFormData({
      formValues: form.getFieldsValue(),
      baggageState: baggageState,
      wizardState: { currentStep, completedSteps: Array.from(completedSteps) },
      paymentsCount: newPaymentsCount  // Use the NEW value, not stale state
    });
  };

  // Build roomsArray (preserved from original)
  let roomsArray = [];
  let passengerIndex = 0;

  if (quoteDetails.room1_adults_count > 0) {
    for (let room_index = 1; room_index <= 4; room_index++) {
      let room;
      switch (room_index) {
        case 1:
          if (quoteDetails.room1_adults_count > 0) {
            room = generatePaxArray(quoteDetails.room1_adults_count, quoteDetails.room1_children_ages || []);
          } else continue;
          break;
        case 2:
          if (quoteDetails.room2_adults_count > 0) {
            room = generatePaxArray(quoteDetails.room2_adults_count, quoteDetails.room2_children_ages || []);
          } else continue;
          break;
        case 3:
          if (quoteDetails.room3_adults_count > 0) {
            room = generatePaxArray(quoteDetails.room3_adults_count, quoteDetails.room3_children_ages || []);
          } else continue;
          break;
        case 4:
          if (quoteDetails.room4_adults_count > 0) {
            room = generatePaxArray(quoteDetails.room4_adults_count, quoteDetails.room4_children_ages || []);
          } else continue;
          break;
        default:
          continue;
      }

      room = room.map((age, idx) => ({
        index: passengerIndex,
        name: passengerIndex,
        key: passengerIndex,
        isListField: true,
        fieldKey: passengerIndex++,
        passenger: getGuestCategory(age).name,
        passenger_he: getGuestCategory(age).name_he,
        passenger_category: getGuestCategory(age).code,
        age: age
      }));

      roomsArray.push({ index: room_index, pax: room });
    }
  } else if (packageDetails?.data?.property?.rooms_occupancies) {
    const rooms_occupancies = packageDetails?.data?.property?.rooms_occupancies;

    for (let room_index = 1; room_index <= 4; room_index++) {
      const adultsKey = `room${room_index}_adults_count`;
      const childrenKey = `room${room_index}_children_ages`;

      if (rooms_occupancies[adultsKey] > 0) {
        const room = generatePaxArray(
          rooms_occupancies[adultsKey],
          rooms_occupancies[childrenKey] || []
        ).map((age, idx) => ({
          index: passengerIndex,
          name: passengerIndex,
          key: passengerIndex,
          isListField: true,
          fieldKey: passengerIndex++,
          passenger: getGuestCategory(age).name,
          passenger_he: getGuestCategory(age).name_he,
          passenger_category: getGuestCategory(age).code,
          age: age
        }));

        roomsArray.push({ index: room_index, pax: room });
      }
    }
  }

  // Calculate totals
  const basePrice = reservationState?.package?.price || 0;
  const totalPrice = calculateTotalPrice(basePrice, totalBaggagePrice, paymentsCount);
  const currency = reservationState?.package?.currency || 'USD';
  const destination = packageDetails?.data?.destination?.city?.name || quoteDetails?.destination?.city_name;

  // Get form values for summaries
  const formValues = form.getFieldsValue();

  // Async quote update - fire and forget, never blocks the flow
  const updateQuoteAsync = (endpoint, data) => {
    axios.post(
      `${API_ENDPOINT}/quote/${quote_uid}/${endpoint}`,
      data,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
        timeout: 5000
      }
    ).catch(error => {
      // Silent fail - log but don't interrupt user flow
      console.warn(`Failed to update quote (${endpoint}):`, error?.message);
    });
    // No await - fire and forget
  };

  // Step completion handlers
  const handleStepContinue = async (stepNumber) => {
    const success = await completeStep(stepNumber);
    if (success) {
      // Persist step data to quote (async, non-blocking)
      const formValues = form.getFieldsValue();

      if (stepNumber === 1) {
        // Owner details
        updateQuoteAsync('add/owner', {
          first_name: formValues.first_name,
          last_name: formValues.last_name,
          email: formValues.email,
          phone: formValues.phone,
          marketing_consent: formValues.marketing_consent || false,
          consent_version: formValues.consent_version || null
        });
      } else if (stepNumber === 2) {
        // Passenger details
        updateQuoteAsync('add/passengers', {
          passengers: formValues.passengers
        });
      } else if (stepNumber === 3) {
        // Baggage selections
        updateQuoteAsync('add/baggage', {
          baggage: baggageState
        });
      }

      // Save progress to localStorage (existing logic)
      saveFormData({
        formValues: formValues,
        baggageState: baggageState,
        wizardState: { currentStep: stepNumber + 1, completedSteps: Array.from(completedSteps) },
        paymentsCount: paymentsCount
      });
    }
  };

  // Handle quote expiration
  const handleQuoteExpire = () => {
    setErrorMessage({
      title: t('reservation.wizard.countdown.expiredTitle'),
      message: t('reservation.wizard.countdown.expiredMessage')
    });
    setOpenError({ status: true, go_back: false });
  };

  // Handle expiration modal close - redirect to deal page
  const handleExpirationClose = () => {
    setOpenError(false);
    if (quoteDetails?.referrer) {
      window.location.href = quoteDetails.referrer;
    } else {
      // Fallback: go back to deal page from URL
      const url_array = window.location.pathname.split('/');
      history.push(url_array[0] + '/' + url_array[1] + '/' + url_array[2]);
    }
  };

  return (
    <div className="reservationPage wizard-layout">
      <MainNavigationBar fixed="true" />

      {/* Modals */}
      <ErrorModal
        errorMessage={errorMessage}
        openError={openError?.status}
        go_back={openError.go_back}
        onClose={handleClose}
        onCloseReturnToDeal={handleCloseReturnToDeal}
      />
      <YesNoModal
        message={yesNoMessage}
        openYesNo={openYesNo}
        onYes={handleYes}
        onNo={handleNo}
      />
      <TermsModal
        link={termLink}
        text={termText}
        open={termsModal}
        onClose={handleCloseTerms}
      />
      <AncillariesModal
        open={openAncillaries}
        onClose={handleCloseAncillaries}
        data={ancillariesMessage}
      />
      <BookingDetailsModal
        open={mobileModalOpen}
        onClose={() => setMobileModalOpen(false)}
        packageDetails={packageDetails}
        quoteDetails={quoteDetails}
        totalPrice={totalPrice}
        baggagePrice={totalBaggagePrice}
        paymentsCount={paymentsCount}
        passengers={formValues.passengers || []}
        currency={currency}
        currentStep={currentStep}
        baggageState={baggageState}
      />

      {/* Hidden accessibility text */}
      <div className="wizard-accessibility-text" tabIndex={0}>
        {t('reservation.wizard.accessibility.supportContact', { code: packageDetails?.data?.code })}
      </div>

      {reservationState && reservationState?.status === "valid" ? (
        <>
          {/* Mobile Summary Bar */}
          <div className="mobile-floating-bar">
            <MobileSummaryBar
                destination={destination}
                departureDate={packageDetails?.data?.departure?.date || packageDetails?.data?.flight?.outbound?.departure?.datetime}
                returnDate={packageDetails?.data?.return?.date || packageDetails?.data?.flight?.inbound?.departure?.datetime}
                nights={packageDetails?.data?.nights}
                onOpenDetails={() => setMobileModalOpen(true)}
                onBackToDeal={() => {
                if (quoteDetails?.referrer) {
                    window.location.href = quoteDetails.referrer;
                } else {
                    const url_array = window.location.pathname.split('/');
                    history.push(url_array[0] + '/' + url_array[1] + '/' + url_array[2]);
                }
                }}
            />

            {/* Mobile Countdown Timer - Fixed at top */}
            <CountdownTimer
                expirationDate={quoteDetails?.expiration}
                onExpire={handleQuoteExpire}
                variant="mobile"
            />
          </div>

          <div className="wizard-container">
            {/* Desktop Countdown Timer - Above sidebar */}
            <div className="wizard-sidebar-wrapper">
              <CountdownTimer
                expirationDate={quoteDetails?.expiration}
                onExpire={handleQuoteExpire}
                variant="desktop"
              />
              {/* Desktop Sidebar */}
              <BookingSidebar
                packageDetails={packageDetails}
                quoteDetails={quoteDetails}
                totalPrice={totalPrice}
                basePrice={basePrice}
                baggagePrice={totalBaggagePrice}
                paymentsCount={paymentsCount}
                currentStep={currentStep}
                passengers={formValues.passengers || []}
                baggageState={baggageState}
                currency={currency}
              />
            </div>

            {/* Main Form Area */}
            <div className="wizard-main">
              <div className="wizard-back-navbar">
                <Navbar className="wizard-navbar-no-margin" name={t('reservation.wizard.navbar.backToDeal')} tabIndex={0} />
              </div>

              <Form
                form={form}
                name="booking_wizard"
                initialValues={{ remember: true }}
                scrollToFirstError={true}
                onFinish={onFinish}
                onValuesChange={handleValuesChange}
                onFinishFailed={onFinishFailed}
              >
                {/* Step 1: Booker Details */}
                <AccordionStep
                  stepNumber={1}
                  title={stepInfo.titles[1]}
                  status={getStepStatus(1)}
                  summaryText={getSummaryForStep(1, formValues, baggageState)}
                  onEdit={() => goToStep(1)}
                >
                  <StepBookerDetails
                    form={form}
                    onContinue={() => handleStepContinue(1)}
                  />
                </AccordionStep>

                {/* Step 2: Passenger Details */}
                <AccordionStep
                  stepNumber={2}
                  title={stepInfo.titles[2]}
                  status={getStepStatus(2)}
                  summaryText={getSummaryForStep(2, formValues, baggageState)}
                  onEdit={() => goToStep(2)}
                >
                  <StepPassengerDetails
                    roomsArray={roomsArray}
                    form={form}
                    onContinue={() => handleStepContinue(2)}
                  />
                </AccordionStep>

                {/* Step 3: Baggage Selection */}
                <AccordionStep
                  stepNumber={3}
                  title={stepInfo.titles[3]}
                  status={getStepStatus(3)}
                  summaryText={getSummaryForStep(3, formValues, baggageState)}
                  onEdit={() => goToStep(3)}
                >
                  <StepBaggageSelection
                    passengers={formValues.passengers || []}
                    roomsArray={roomsArray}
                    baggageOptions={reservationState?.flight?.baggage || []}
                    baggageState={baggageState}
                    onBaggageChange={changeBaggage}
                    onContinue={() => handleStepContinue(3)}
                    currency={currency}
                  />
                </AccordionStep>

                {/* Step 4: Order Summary */}
                <AccordionStep
                  stepNumber={4}
                  title={stepInfo.titles[4]}
                  status={getStepStatus(4)}
                  onEdit={() => goToStep(4)}
                >
                  <StepOrderSummary
                    packageDetails={packageDetails}
                    quoteDetails={quoteDetails}
                    passengers={formValues.passengers || []}
                    baggageState={baggageState}
                    totalPrice={totalPrice}
                    onContinue={() => handleStepContinue(4)}
                    currency={currency}
                  />
                </AccordionStep>

                {/* Step 5: Payment & Approval */}
                <AccordionStep
                  stepNumber={5}
                  title={stepInfo.titles[5]}
                  status={getStepStatus(5)}
                  summaryText={getSummaryForStep(5, formValues, baggageState)}
                  onEdit={() => goToStep(5)}
                >
                  <StepPaymentApproval
                    packageDetails={packageDetails}
                    quoteDetails={quoteDetails}
                    basePrice={basePrice}
                    totalBaggagePrice={totalBaggagePrice}
                    paymentsCount={paymentsCount}
                    onPaymentsChange={handlePaymentsChange}
                    calculateTotalPrice={calculateTotalPrice}
                    calculatePaymentPrice={calculatePaymentPrice}
                    onOpenLink={onOpenLink}
                    finishProgress={finishProgress}
                    currency={currency}
                    form={form}
                    onSubmit={() => form.submit()}
                    marketingConsentGiven={formValues.marketing_consent === true && formValues.consent_version === 'v1_full'}
                  />
                </AccordionStep>
              </Form>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center mt-5">
          <CircularProgress className="wizard-loading-progress" />
        </div>
      )}
    </div>
  );
};


    /**
     * Calculate the number of days / weeks / months between the departure date & today
     * @param {*} departure_date 
     */
    const GetTimeToDeparture = (departure_date) => {
      try {
          const today = new Date();
          const [datestr, timestr] = (departure_date+'').split("T");
          const [year, month, day] = datestr.split("-");
          const departureDate = new Date(+year, +month - 1, +day);

          const difference = departureDate.getTime() - today.getTime();

          const daysToDeparture = Math.ceil(difference / (1000 * 60 * 60 * 24));
          
          switch (true) {
              case daysToDeparture <= 2: return '1-2 days';break;
              case daysToDeparture <= 4: return '3-4 days';break;
              case daysToDeparture <= 7: return '5-7 days';break;
              case daysToDeparture <= 10: return '8-10 days';break;
              case daysToDeparture <= 13: return '11-13 days';break;
              case daysToDeparture <= 21: return '2 weeks';break;
              case daysToDeparture <= 28: return '3 weeks';break;
              case daysToDeparture <= 35: return '4 weeks';break;
              case daysToDeparture <= 42: return '5 weeks';break;
              case daysToDeparture <= 49: return '6 weeks';break;
              case daysToDeparture <= 56: return '7 weeks';break;
              case daysToDeparture <= 63: return '8 weeks';break;
              case daysToDeparture <= 70: return '9 weeks';break;
              case daysToDeparture <= 100: return '2 months';break;
              case daysToDeparture <= 130: return '3 months';break;
              case daysToDeparture <= 160: return '4 months';break;
              case daysToDeparture <= 190: return '5 months';break;
              case daysToDeparture <= 220: return '6 months';break;
              case daysToDeparture <= 250: return '7 months';break;
              case daysToDeparture <= 280: return '8 months';break;
              case daysToDeparture <= 310: return '9 months';break;
              case daysToDeparture > 310: return '10+ months';break;
              default: return '';
          }
      } catch(e) {
          return 'ERROR'
      }
      
    }

export default ReservationWizard;
